import React, { ChangeEvent, FC, useCallback, useEffect, useMemo } from "react";
import { useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { QuestionColumn } from "../QuestionsColumn/QuestionsColumn";
import { Question } from "../QuestionsColumn/Question/Questions";
import {
  Category,
  IQuestion,
  QUESTION_TYPE,
  QuestionPayload,
  answerType,
  answerTypeIcon,
} from "../../../models/SurveyModels";
import { KeyStorage, getStorageItem } from "../../../utils/localStorage";
import { useTranslation } from "react-i18next";
import Button from "../../../components/Button/Button";
import { useComponentVisible } from "../../../utils/functions";
import Modal from "../../../components/Modal/Modal";
import FormCreateQuestion from "../../../components/Forms/FormCreateQuestion/FormCreateQuestion";
import { useEndpoints } from "../../../hooks/useEndpoints";
import getClasses from "../../../utils/getClasses";
import { IOption } from "../../../components/Forms/FormInput/FormSelect/FormSelect";
import { useToastAlert } from "../../../components/ToastAlert/ToastAlert";
import { DURATION_TOAST } from "../../../utils/costants";
import { IErrorsCompanies } from "../../../models/CompaniesModel";

require("./Template.scss");
export const COLUMN_NAMES = {
  QUESTION: "questions",
  TEMPLATE: "template",
};

export interface QuestionType extends IQuestion {
  column: string;
}

export type IQuestionsButtonOptions = {
  onClick?: (item: QuestionType) => void | Promise<void>;
  isLoading?: boolean;
  colorTheme: "green" | "blue" | "red";
  label: string | JSX.Element | boolean;
  icon: JSX.Element;
}[];
interface ITemplate {
  onChange: (value: IQuestion[]) => void;
  templateDataQuestions?: IQuestion[];
  selectedRoleFilter?: string;
  templateDataName?: string;
  arrayFilterSubCategory: Category[];
  selectedFilterCateory?: string;
}
export const Template: FC<ITemplate> = ({
  onChange,
  templateDataQuestions,
  templateDataName,
  selectedFilterCateory,
  selectedRoleFilter,
  arrayFilterSubCategory,
}) => {
  const [questions, setQuestions] = useState<QuestionType[]>([]);
  const { t } = useTranslation();
  const [isOpenModal, setOpenModal] = useState<boolean>(false);

  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const { getAllQuestions, createQuestion, getQuestionbyId } = useEndpoints();
  const [inputValue, setInputValue] = React.useState("");
  const [filterSubCategory, setFilterSubCategory] = useState<
    Category | string | ""
  >(selectedFilterCateory || "");
  const [categoriesFormQuestions, setCategoriesFormQuestions] = useState<
    IOption[]
  >([]);

  const [answer, setAnswer] = useState<IOption[]>([]);
  const { showToast } = useToastAlert();

  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [limit, setLimit] = useState<number>(0);
  const [count, setCount] = useState<number>(1);
  const [skip, setSkip] = useState<number>(0);

  const moveCardHandler = (dragIndex: number, hoverIndex: number) => {
    const dragItem = questions[dragIndex];
    if (dragItem) {
      setQuestions((prevState) => {
        const coppiedStateArray = [...prevState];
        const prevItem = coppiedStateArray.splice(hoverIndex, 1, dragItem)[0];
        coppiedStateArray.splice(dragIndex, 1, prevItem);
        return coppiedStateArray;
      });
    }
  };
  useEffect(() => {
    if (skip < count) {
      setIsLoadingQuestions(true);
      getAllQuestions({
        category:
          typeof filterSubCategory === "string"
            ? filterSubCategory
            : (filterSubCategory as Category)?.id,
        text: debouncedInputValue,
        skip: skip,
        role: selectedRoleFilter,
        limit: 30,
      }).then(({ items, limit, count }) => {
        const questionsForDrag = items?.map((question: IQuestion) => {
          return { ...question, column: COLUMN_NAMES.QUESTION };
        });

        (filterSubCategory as Category)?.id &&
          setQuestions((prevState) => {
            if (prevState) {
              const templateQuestions = prevState?.filter(
                (question) => question.column === COLUMN_NAMES.TEMPLATE
              );
              const filteredQuestions = questionsForDrag?.filter(
                (question) => question.column === COLUMN_NAMES.TEMPLATE
              );
              return [...templateQuestions, ...filteredQuestions];
            } else {
              return questionsForDrag;
            }
          });
        if (!filterSubCategory && skip === 0) {
          setQuestions((prevState) =>
            prevState.filter(
              (question) => question.column === COLUMN_NAMES.TEMPLATE
            )
          );
        }
        setQuestions((prevState) => {
          if (prevState) {
            const existingIds = prevState.map((item) => item.id);
            const uniqueItems = questionsForDrag.filter(
              (newItem) => !existingIds.includes(newItem.id)
            );
            return [...prevState, ...uniqueItems];
          } else return [...questionsForDrag];
        });

        count && setCount(count);
        setLimit(limit);
        setIsLoadingQuestions(false);
      });
    }
  }, [
    count,
    getAllQuestions,
    debouncedInputValue,
    limit,
    skip,
    filterSubCategory,
  ]);

  useEffect(() => {
    const wrapperQuestions = document.getElementById(
      "id-questions"
    ) as HTMLElement;
    wrapperQuestions !== null &&
      wrapperQuestions?.addEventListener("scroll", () => {
        (wrapperQuestions.lastChild as HTMLDivElement)?.getBoundingClientRect()
          ?.bottom < wrapperQuestions?.getBoundingClientRect()?.bottom &&
          setSkip(skip + limit);
      });
    return () => {
      wrapperQuestions?.removeEventListener("scroll", () => {});
    };
  }, [limit, skip]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setSkip(0);
  };

  useEffect(() => {
    const options: IOption[] = arrayFilterSubCategory.map(
      (category: Category) => {
        return {
          [category.name]: category.id,
          [category.icon]: "icon",
          [category.color]: "color",
          [category.background]: "background",
        };
      }
    );
    setCategoriesFormQuestions(options);
    const answerWhitIcon = answerType.map((item, index) => {
      return { ...item, ...answerTypeIcon[index] };
    });
    setAnswer(answerWhitIcon);
  }, [arrayFilterSubCategory]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    if (templateDataQuestions) {
      const questionTemplate = templateDataQuestions.map((item) => ({
        ...item,
        column: COLUMN_NAMES.TEMPLATE,
      }));
      setQuestions(questionTemplate);
    }
  }, [getQuestionbyId, templateDataQuestions]);

  useEffect(() => {
    const filteredIds = questions
      ?.filter(({ column }) => column === COLUMN_NAMES.TEMPLATE)
      .map((id) => id);
    filteredIds && onChange && onChange(filteredIds);
  }, [questions, onChange]);

  const generateButtons = useCallback(
    (
      callback: (
        value: NonNullable<any>,
        role: KeyStorage["me"]["role"]
      ) => IQuestionsButtonOptions
    ): Array<IQuestionsButtonOptions> => {
      if (!questions) return [] as Array<IQuestionsButtonOptions>;
      return questions.map((company) => {
        return callback(company, getStorageItem("me")!.role);
      });
    },
    [questions]
  );

  const onSubmitFromQuestions = async (data: QuestionPayload) => {
    const response = await createQuestion({
      ...data,
      type: QUESTION_TYPE.STANDARD,
    });
    if (response.status === 201) {
      const questionCreated: IQuestion = await response.json();
      showToast({
        message: `${t("home.questions.toast.success.create")}`,
        type: "success",
        duration: DURATION_TOAST,
      });
      setQuestions((prevState) => {
        return [
          ...prevState,
          { ...questionCreated, column: COLUMN_NAMES.TEMPLATE },
        ];
      });
      setOpenModal(false);
    }
    if (response.status === 400) {
      const { errors } = await response.json();
      errors.map(({ value, msg, param }: IErrorsCompanies) =>
        showToast({
          message: `${msg} ${param}`,
          subMessage: value,
          type: "error",
        })
      );
    }
    if (response.status === 500) {
      showToast({
        message: `${t("home.companies.toast.error.generic")}`,
        type: "error",
      });
    }
  };
  const { ref, setIsComponentVisible, isComponentVisible } =
    useComponentVisible(false);
  const buttonsOption = useMemo(
    () =>
      generateButtons((resource: any) => {
        let buttons: IQuestionsButtonOptions = [];
        buttons.push({
          onClick: (item: QuestionType) => {
            setQuestions((prevState: QuestionType[]) => {
              return prevState.map((QUESTION: QuestionType) => {
                return {
                  ...QUESTION,
                  column:
                    QUESTION.id === item.id
                      ? COLUMN_NAMES.QUESTION
                      : QUESTION.column,
                };
              });
            });
          },
          label: `${t("createTemplate.removeQuestion")}`,
          colorTheme: "red",
          icon: <i className="icon icon-delete" />,
        });
        return buttons;
      }),
    [generateButtons, t]
  );

  const filtersButton = () => {
    return (
      <div className="table-options-menu" ref={ref}>
        <div>
          <div
            className="wrapper-options"
            onClick={() => {
              setIsComponentVisible((old) => !old);
            }}
          >
            <i className={`icon icon-filters`}></i>
          </div>
          <div className="container-options category">
            {isComponentVisible &&
              arrayFilterSubCategory.map((item, index) => (
                <div
                  className={`filters-item ${getClasses({
                    active:
                      (typeof filterSubCategory === "string" &&
                        item.id === filterSubCategory) ||
                      item.id === (filterSubCategory as Category)?.id,
                  })}`}
                  key={index}
                >
                  <div
                    onClick={() => {
                      filterSubCategory === item
                        ? setFilterSubCategory("")
                        : setFilterSubCategory(item);
                      setSkip(0);
                      setIsComponentVisible((old) => !old);
                    }}
                    className="menu-item d-flex justify-content-between gap-1"
                  >
                    <div>
                      <i
                        style={{
                          color: item.color,
                        }}
                        className={`icon ${item.icon}`}
                      ></i>
                      <span>{item.name}</span>
                    </div>
                    {((typeof filterSubCategory === "string" &&
                      item.id === filterSubCategory) ||
                      item.id === (filterSubCategory as Category)?.id) && (
                      <i className="icon icon-checkmark icon-square-checked me-1"></i>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };
  const returnItemsForColumn = (placeName: string) => {
    return questions
      .filter((item) => item.column === placeName)
      .map((item, index) => (
        <Question
          {...item}
          key={item.id + index}
          currentColumnName={item.column}
          setItems={setQuestions}
          onChange={onChange}
          index={index}
          moveCardHandler={moveCardHandler}
          buttonsOption={item.column === "template" ? buttonsOption : undefined}
        />
      ));
  };
  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className="create-template-container ">
          <div className="row">
            <div className=" col-lg-8 col-xl-8 ">
              <div>
                <h1>{templateDataName || "Template"}</h1>
                <QuestionColumn
                  isLoading={false}
                  items={questions}
                  className={COLUMN_NAMES.TEMPLATE}
                  title={COLUMN_NAMES.TEMPLATE}
                  lenghtItems={
                    returnItemsForColumn(COLUMN_NAMES.TEMPLATE).length
                  }
                >
                  {returnItemsForColumn(COLUMN_NAMES.TEMPLATE)}
                </QuestionColumn>
              </div>

              <div className="my-3">
                <Button onClick={() => setOpenModal(true)} fullWidth outline>
                  {t("Nuova domanda")} +
                </Button>
              </div>
            </div>
            <div className=" col-lg-4 col-xl-4 ">
              <div className="wrapper-questions">
                <h5>Aggiungi Domande</h5>
                <div className="my-3">
                  <Button onClick={() => setOpenModal(true)} fullWidth outline>
                    {t("Nuova domanda")} +
                  </Button>
                </div>
                <div className="resources-searching">
                  <div className="form-group">
                    <input
                      id="searchResource"
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      className="form-control filter-questions"
                      placeholder={
                        t("home.questions.inputPlaceholder") as string
                      }
                    />
                    <i className="icon-search"></i>
                    {inputValue && (
                      <i
                        className="icon-close"
                        onClick={() => {
                          setInputValue("");
                        }}
                      ></i>
                    )}
                  </div>
                  <>{filtersButton()}</>
                </div>
                <QuestionColumn
                  isLoading={isLoadingQuestions}
                  title={COLUMN_NAMES.QUESTION}
                  lenghtItems={
                    returnItemsForColumn(COLUMN_NAMES.QUESTION).length
                  }
                  className={COLUMN_NAMES.QUESTION}
                >
                  {returnItemsForColumn(COLUMN_NAMES.QUESTION)}
                </QuestionColumn>
              </div>
            </div>
          </div>
        </div>
      </DndProvider>
      {isOpenModal && (
        <Modal
          close={() => setOpenModal(false)}
          open={isOpenModal}
          classWrapper="form-create-questions col-10"
          children={
            <div>
              <div className="content">
                <FormCreateQuestion
                  onSumbitForm={onSubmitFromQuestions}
                  isInModal
                  categories={categoriesFormQuestions}
                  answerType={answer}
                  setOpenModal={(value) => setOpenModal(value)}
                />
              </div>
            </div>
          }
        />
      )}
    </>
  );
};
